import { GitMerge, Home } from "lucide-react"
import { cn } from "@/lib/utils/helpers"
import { SearchableSelect } from "@/components/ui/SearchableSelect"
import { SectionCard } from "@/components/ui/SectionCard"
import companyFormStyles from "@/features/company/styles/companyAdd.module.css"
import { transferSearchableTriggerClass } from "../../../constants/transferProcessStyles"
import { usesTransferWizardCardLayout } from "../../../utils/transferPropertyCompanyWizard"
import type { TransferPropertyInfoSectionProps } from "./propertyInfoSectionTypes"

export function TransferPropertyMergeResidentRow({ ctx }: TransferPropertyInfoSectionProps) {
  const {
    fragment,
    companyList,
    propertyList,
    fkCompanyId,
    readOnlyControl,
    register,
    watch,
    setValueValidated,
    loadCompany,
    debouncedLoadCompanySearch,
    loadProperty,
    searchProperty,
    companyOptionsLoading,
    propertyOptionsLoading,
    updateStaffandResident,
  } = ctx

  const useCardLayout = usesTransferWizardCardLayout(fragment)
  const transferResidentFieldGridClass = useCardLayout
    ? "grid grid-cols-1 gap-6 md:grid-cols-3"
    : companyFormStyles.formRow
  const showCompanyRequired = !String(fkCompanyId ?? "").trim()
  const showPropertyRequired = !String(watch("property_id") ?? "").trim()

  const fields = (
    <div
      data-testid="transfer-process-property-form-merge-resident-row"
      className={transferResidentFieldGridClass}
    >
      <div className={useCardLayout ? undefined : companyFormStyles.formCol}>
        <div className={companyFormStyles.formGroup}>
          <label
            data-testid="transfer-process-property-form-source-property-label"
            className={companyFormStyles.formLabel}
          >
            {fragment === "merge_property" ? "Merge From" : "Transfer From"}
          </label>
          <input
            type="text"
            readOnly
            data-testid="transfer-process-property-form-source-property-input"
            className={cn(companyFormStyles.customFormControl, "bg-[#f8fafc]")}
            {...register("property_name")}
          />
        </div>
      </div>
      <div className={useCardLayout ? undefined : companyFormStyles.formCol}>
        <div className={companyFormStyles.formGroup}>
          <label
            data-testid="transfer-process-property-form-select-company-label"
            className={companyFormStyles.formLabel}
          >
            Select Company<span className={companyFormStyles.requiredStar}>*</span>
          </label>
          <SearchableSelect
            className={transferSearchableTriggerClass}
            placeholder="Select Company"
            options={companyList}
            value={fkCompanyId}
            triggerDataTestId="transfer-process-property-form-merge-company-select"
            onChange={(v) => {
              const id = Array.isArray(v) ? v[0] : v
              setValueValidated("fk_company_id", id)
              void loadCompany()
              void loadProperty()
            }}
            onOpen={() => void loadCompany(undefined, { silent: true })}
            onSearch={(s) => debouncedLoadCompanySearch(s)}
            invokeSearchOnClose={false}
            loading={companyOptionsLoading}
            disabled={readOnlyControl.includes("company")}
          />
          {showCompanyRequired ? (
            <p
              data-testid="transfer-process-property-form-merge-company-error"
              className="mt-1.5 text-xs text-rose-600"
            >
              Company Selection is required.
            </p>
          ) : null}
        </div>
      </div>
      <div className={useCardLayout ? undefined : companyFormStyles.formCol}>
        <div className={companyFormStyles.formGroup}>
          <label
            data-testid="transfer-process-property-form-target-property-label"
            className={companyFormStyles.formLabel}
          >
            {fragment === "merge_property" ? "Merge To" : "Transfer To Property"}
            <span className={companyFormStyles.requiredStar}>*</span>
          </label>
          <SearchableSelect
            className={transferSearchableTriggerClass}
            placeholder="Select Property"
            options={propertyList}
            value={watch("property_id")}
            triggerDataTestId="transfer-process-property-form-target-property-select"
            onChange={(v) => {
              const id = Array.isArray(v) ? v[0] : v
              setValueValidated("property_id", id)
              updateStaffandResident(id)
            }}
            onOpen={() => void loadProperty(undefined, { silent: true })}
            onSearch={(s) => searchProperty(s)}
            invokeSearchOnClose={false}
            loading={propertyOptionsLoading}
            disabled={readOnlyControl.includes("property")}
          />
          {showPropertyRequired ? (
            <p
              data-testid="transfer-process-property-form-target-property-error"
              className="mt-1.5 text-xs text-rose-600"
            >
              Property is required
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )

  if (useCardLayout) {
    return (
      <SectionCard
        icon={fragment === "merge_property" ? GitMerge : Home}
        title="Property Information"
        testId="transfer-process-property-info-section"
        titleTestId="transfer-process-property-info-section-title"
      >
        {fields}
      </SectionCard>
    )
  }

  return fields
}
